import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { orderStore } from '../stores/OrderStore';
import { OrderCard } from '../components/OrderCard';
import { OrderDetailsModal } from '../components/OrderDetailsModal';
import type { Order } from '../types/order';

export const OrdersPage: React.FC = observer(() => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  useEffect(() => {
    // Fetch orders on mount
    orderStore.fetchOrders();
  }, []);

  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedOrder(null);
  };

  const handleStatusUpdate = async (orderId: string, status: string) => {
    await orderStore.updateOrderStatus(orderId, status);
  };

  const newOrders = orderStore.newOrders.pending;
  const acceptedOrders = orderStore.newOrders.inprogress;

  return (
    <>
      {orderStore.loading && orderStore.orders.length === 0 ? (
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : (
        <div className="h-full overflow-y-auto px-4 py-4">
          {/* Order Tabs */}
          <div className="flex gap-4 mb-4">
            {/* New Orders */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900">New Orders</h2>
                <div className="bg-[#FF5722] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  {newOrders.length}
                </div>
              </div>
              <div className="space-y-2">
                {newOrders.length === 0 ? (
                  <div className="bg-white rounded p-4 text-center text-gray-500 text-sm">
                    No new orders
                  </div>
                ) : (
                  newOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      type="new"
                      onViewDetails={handleViewDetails}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Accepted Orders */}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-base font-semibold text-gray-900">Accepted Orders</h2>
                <div className="bg-[#00B2A8] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                  {acceptedOrders.length}
                </div>
              </div>
              <div className="space-y-2">
                {acceptedOrders.length === 0 ? (
                  <div className="bg-white rounded p-4 text-center text-gray-500 text-sm">
                    No accepted orders
                  </div>
                ) : (
                  acceptedOrders.map((order) => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      type="accepted"
                      onViewDetails={handleViewDetails}
                      onStatusUpdate={handleStatusUpdate}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      <OrderDetailsModal
        order={selectedOrder}
        visible={isModalVisible}
        onClose={handleCloseModal}
        type={selectedOrder?.status === 'PENDING' ? 'new' : 'accepted'}
        onStatusUpdate={handleStatusUpdate}
      />
    </>
  );
});
